import React from 'react';
import { object } from 'yup';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { passwordRequirements } from '@whitelabel/helpers/forms/requirements';
import { Button, Loading } from '@whitelabel/component-library';
import commonMessages from '@whitelabel/helpers/messages/commonMsg';
import { getPhoneSchema, getEmailSchema, getPasswordSchema } from '@whitelabel/helpers/forms/schema';
import { SOURCE_SMS, SOURCE_EMAIL } from '@whitelabel/xcover-shared/helpers/constants';
import { RootState } from '../../../store';
import { filterGeneratedEmail } from '../../../helpers/utils';
import { confirmSignUp } from '../../../actions';
import { SignUpError, SignUpCommonError, SignUpTokenError } from '../../../helpers/types';
import messages from './messages';
import {
  StyledAlert,
  StyledFormikEmailInput,
  StyledFormikPasswordInput,
  StyledFormikPhoneInput,
} from './styledConfirmSignUpForm';

interface IValues {
  email: string;
  password: string;
  phone: string;
}
interface IConfirmSignUpForm {
  customer?: RootState['customer']['data'];
  id?: string; // TODO: ExtendedPropTypes.uuid
  source?: string;
  loading: boolean;
  formDisabled?: boolean;
  signUpToken?: string | null;
  tokenErrorMessage?: string;
  onSubmitOnModal?: any;
  autoFocusPasswordField?: boolean;
}

const ConfirmSignUpForm = ({
  customer,
  id,
  source,
  loading,
  formDisabled,
  signUpToken,
  tokenErrorMessage,
  onSubmitOnModal,
  autoFocusPasswordField,
}: IConfirmSignUpForm): JSX.Element => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const initialValues = {
    email: filterGeneratedEmail(customer?.email) || '',
    password: '',
    phone: customer?.phone || '',
  };

  const validationSchema = object({
    email: getEmailSchema(source === SOURCE_EMAIL),
    password: getPasswordSchema(intl),
    phone: getPhoneSchema(intl, source === SOURCE_SMS),
  });

  const onSubmit = (values: IValues, { setSubmitting, setErrors }: FormikHelpers<IValues>) => {
    if (onSubmitOnModal) {
      onSubmitOnModal({
        values,
        setSubmitting,
        setErrors,
      });
    } else {
      dispatch(confirmSignUp({ ...values, id, setSubmitting, source, setErrors, signUpToken }));
    }
  };

  const renderEmailField = (readonly: boolean) => (
    <Field
      component={StyledFormikEmailInput}
      id="confirmSignUpEmail"
      name="email"
      type="email"
      autoComplete="email"
      label={intl.formatMessage(messages.emailLabel)}
      helpText={
        readonly
          ? intl.formatMessage(messages.disabledHelpText, {
              disabledField: intl.formatMessage(messages.emailAddress),
            })
          : intl.formatMessage(messages.optionalHelpText)
      }
      readOnly={formDisabled || readonly}
    />
  );

  const renderPasswordField = () => (
    <Field
      component={StyledFormikPasswordInput}
      id="confirmSignUpPassword"
      name="password"
      type="password"
      autoComplete="new-password"
      label={intl.formatMessage(messages.passwordLabel)}
      requirements={passwordRequirements(intl)}
      readOnly={formDisabled}
      autoFocus={autoFocusPasswordField}
    />
  );

  const renderPhoneField = (disabled: boolean) => (
    <Field
      component={StyledFormikPhoneInput}
      id="confirmSignUpPhone"
      name="phone"
      autoComplete="tel"
      type="tel"
      label={intl.formatMessage(messages.phoneLabel)}
      helpText={
        disabled
          ? intl.formatMessage(messages.disabledHelpText, {
              disabledField: intl.formatMessage(messages.phoneNumber),
            })
          : intl.formatMessage(messages.optionalHelpText)
      }
      disabled={formDisabled || disabled}
    />
  );

  const renderFields = () => {
    if (source === SOURCE_EMAIL) {
      return (
        <>
          {renderEmailField(true)}
          {renderPasswordField()}
          {renderPhoneField(false)}
        </>
      );
    }
    return (
      <>
        {renderPhoneField(true)}
        {renderPasswordField()}
        {renderEmailField(false)}
      </>
    );
  };

  const getErrorMessage = (errors: SignUpError) => {
    if ((errors as SignUpCommonError).showCommonError) return intl.formatMessage(commonMessages.error);
    if ((errors as SignUpTokenError).showTokenError) return tokenErrorMessage;
    return intl.formatMessage(commonMessages.completeForm);
  };

  if (!formDisabled && (loading || !customer)) {
    return <Loading />;
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ isSubmitting, errors, submitCount }) => (
        <Form>
          {submitCount > 0 && !!Object.keys(errors).length && (
            <StyledAlert color="danger">{getErrorMessage(errors)}</StyledAlert>
          )}
          {renderFields()}
          <Button
            type="submit"
            loading={isSubmitting}
            color="primary"
            data-analytics="confirmSignup"
            disabled={formDisabled}
          >
            {intl.formatMessage(messages.continueButtonLabel)}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default ConfirmSignUpForm;
